/**
 * What the customer still has to hand over, which is not the same as what the order cost.
 *
 * The order detail screen showed one number for every case: the goods plus the fee,
 * whether or not either had been paid. On an order settled online with the delivery fee
 * included that read as the full amount all over again — the sentence somebody argues
 * with while standing at the door holding their wallet.
 *
 * Four states, and they need four different answers.
 */

/**
 * @param {object} order — as `GET /api/orders/:orderId` presents it
 * @param {boolean} paysCash — from `order.paymentType`, which is present on every order
 *   including the ones placed when there were eight payment methods
 *
 * @returns {{goods: number, fee: number|null, total: number, settled: boolean, feeKnown: boolean}}
 *   `fee` is **null** when the delivery fee is not yet final — an unquoted order still
 *   waiting for a rider. That is the one case where a total cannot honestly be stated, and
 *   null says so rather than passing a zero off as an answer.
 */
export function amountOwed(order, paysCash) {
  /**
   * Whether the fee on this order is the final figure.
   *
   * The server decides, because this cannot be worked out from the numbers: an absent
   * `riderDistanceFee` and one priced at zero both arrive as 0, and they mean opposite
   * things. The fallback covers a response from an API older than `deliveryFeeFinal`.
   */
  const feeKnown = order?.deliveryFeeFinal ?? Boolean(order?.rider)

  // Nothing has been paid on a cash order, so both halves are owed at the door.
  const goods = paysCash ? Number(order?.estimatedTotal) || 0 : 0

  /**
   * `deliveryFeePaid` is forced false on a cash order by both the form and the server, so
   * the cash branch needs no special case: it falls through to the fee being owed.
   */
  const fee = order?.deliveryFeePaid
    ? 0
    : (feeKnown ? Number(order?.deliveryFee) || 0 : null)

  const total = goods + (fee ?? 0)

  return {
    goods,
    fee,
    total,
    feeKnown,
    /**
     * Nothing left to hand over, and known to be nothing.
     *
     * Deliberately false while the fee is unknown — a zero that might still grow is not
     * the same as a bill that is closed, and only one of them should say *"already paid"*.
     */
    settled: fee !== null && total === 0,
  }
}
